import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import type { CloneAction, CloneDetail } from '@api/api';
import { endpoints, qk } from '../../api/endpoints';
import { isActiveJob } from '../../api/useJobStream';
import { EmptyState, HealthDot, KeyValue, RelTime, SectionCard } from '../../components/common';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { KpiTile } from '../../components/KpiTile';
import { jobLabel } from '../../components/JobView';
import { fmtAgeHours, fmtBytes, fmtDate, fmtMoney, fmtNumber } from '../../lib/format';
import { MONO } from '../../theme';
import { useIsOwner } from '../../auth/AuthProvider';
import { useCloneAction } from './useCloneAction';

interface Pending {
  action: CloneAction;
  title: string;
  text: string;
  label: string;
  danger?: boolean;
  typed?: string;
}

export function CloneOverviewTab({ clone: c }: { clone: CloneDetail }) {
  const owner = useIsOwner();
  const run = useCloneAction(c.id);
  const [pending, setPending] = useState<Pending | null>(null);
  const [deployOpen, setDeployOpen] = useState(false);
  const [releaseId, setReleaseId] = useState<number | ''>('');
  const jobs = useQuery({ queryKey: qk.cloneJobs(c.id), queryFn: () => endpoints.cloneJobs(c.id), refetchInterval: 15_000 });
  const releases = useQuery({ queryKey: qk.releases, queryFn: endpoints.releases, enabled: deployOpen });
  const activeJob = jobs.data?.find((j) => j.id === c.activeJobId) ?? jobs.data?.find(isActiveJob);
  const h = c.latestHealth;
  const s = c.stats;
  const busy = !!activeJob || run.isPending;
  const live = c.status === 'running' || c.status === 'stopped' || c.status === 'failed';
  // CloneDetail.actions says what the status allows; fall back to local rules for older servers
  const can = (a: CloneAction, local: boolean) => !busy && (c.actions && a in c.actions ? !!c.actions[a] : local);

  const ask = (p: Pending) => setPending(p);
  const confirm = () => {
    if (!pending) return;
    run.mutate({ action: pending.action, body: pending.action === 'delete' ? { confirmSlug: c.slug } : {} }, { onSettled: () => setPending(null) });
  };

  return (
    <Stack spacing={2}>
      {activeJob && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" component={RouterLink} to={`/clones/${c.id}/jobs?job=${activeJob.id}`}>
              Open job
            </Button>
          }
        >
          {jobLabel(activeJob.type)} is running (step {activeJob.steps.filter((x) => x.status === 'done').length + 1} of {activeJob.steps.length}).
        </Alert>
      )}

      {c.status === 'draft' && (
        <SectionCard title="Ready to provision?">
          <Stack spacing={1}>
            {(
              [
                [true, 'Client and domain', 'settings'],
                [!!c.releaseId, c.releaseId ? `Release ${c.release ?? ''}` : 'Release — none chosen', 'settings'],
                [c.tenantComplete, 'Tenant answers (brand, currency, city, admin)', 'tenant'],
                [c.envComplete, c.envComplete ? 'Provider keys' : `Provider keys — missing ${c.missingRequired.join(', ')}`, 'keys'],
              ] as const
            ).map(([ok, label, tab]) => (
              <Stack key={label} direction="row" spacing={1} alignItems="center">
                {ok ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="warning" fontSize="small" />}
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {label}
                </Typography>
                {!ok && (
                  <Button size="small" component={RouterLink} to={`/clones/${c.id}/${tab}`}>
                    Fill in
                  </Button>
                )}
              </Stack>
            ))}
            {owner && (
              <Box sx={{ pt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<RocketLaunchOutlinedIcon />}
                  disabled={!c.tenantComplete || !c.envComplete || !c.releaseId || !can('provision', true)}
                  onClick={() => ask({ action: 'provision', title: 'Provision this clone?', text: `Creates the stack, CRM, TLS and tenant on x123 for ${c.domain}. Takes 10–15 minutes.`, label: 'Provision' })}
                >
                  Provision
                </Button>
              </Box>
            )}
          </Stack>
        </SectionCard>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <SectionCard
            title="Health now"
            action={
              h && (
                <Typography variant="caption" color="text.secondary">
                  <RelTime iso={h.ts} prefix="checked " />
                </Typography>
              )
            }
          >
            {!h ? (
              <EmptyState title="No health data yet" text={c.status === 'draft' ? 'The collector starts checking once the clone is provisioned.' : 'Waiting for the first check (every 60 s).'} />
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <HealthDot health={h.health} label />
                  <Typography variant="body2">
                    Errors last minute: <strong>{h.errors1m}</strong>
                  </Typography>
                  {h.resources && (
                    <Typography variant="body2">
                      CPU <strong>{h.resources.cpuPct}%</strong> · RAM <strong>{fmtBytes(h.resources.memBytes)}</strong>
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(h.checks).map(([k, v]) => (
                    <Chip key={k} size="small" variant="outlined" color={v === 'ok' ? 'success' : v === 'fail' ? 'error' : 'warning'} label={`${k}: ${v}`} />
                  ))}
                </Stack>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Services">
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Health</TableCell>
                        <TableCell align="right">Restarts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {h.services.map((sv) => (
                        <TableRow key={sv.name}>
                          <TableCell sx={{ fontFamily: MONO, fontSize: 13 }}>{sv.name}</TableCell>
                          <TableCell sx={{ color: sv.state === 'running' ? 'success.main' : 'error.main' }}>{sv.state}</TableCell>
                          <TableCell sx={{ color: sv.health === 'unhealthy' ? 'error.main' : undefined }}>{sv.health ?? '—'}</TableCell>
                          <TableCell align="right" sx={{ color: sv.restarts >= 3 ? 'warning.main' : undefined }}>
                            {sv.restarts}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                {h.lastErrors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Last errors
                    </Typography>
                    <Box sx={{ fontFamily: MONO, fontSize: 12.5, bgcolor: 'action.hover', borderRadius: 1, p: 1.5, color: 'error.main' }}>
                      {h.lastErrors.map((e, i) => (
                        <div key={i} style={{ wordBreak: 'break-word' }}>
                          {e}
                        </div>
                      ))}
                    </Box>
                    <Button size="small" component={RouterLink} to={`/clones/${c.id}/logs?level=error`} sx={{ mt: 0.5 }}>
                      Open error logs
                    </Button>
                  </Box>
                )}
              </Stack>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <SectionCard title="Status">
              <KeyValue
                items={[
                  ['Status', c.status],
                  ['Release', c.release ?? '—'],
                  ['Backup', c.backupAgeHours === null ? '—' : `${fmtAgeHours(c.backupAgeHours)} ago${h?.backup?.sizeBytes ? ` · ${fmtBytes(h.backup.sizeBytes)}` : ''}`],
                  ['TLS', c.tlsDaysLeft === null ? '—' : `${c.tlsDaysLeft} days left`],
                  ['Ports', c.appPort ? `${c.appPort} / ${c.socketPort}` : '—'],
                  ['Created', fmtDate(c.createdAt)],
                ]}
              />
            </SectionCard>
            {owner && (
              <SectionCard title="Actions">
                <Stack spacing={1}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button fullWidth variant="outlined" startIcon={<RestartAltIcon />} disabled={!can('restart', c.status === 'running')} onClick={() => ask({ action: 'restart', title: 'Restart the stack?', text: 'Riders may see errors for up to a minute.', label: 'Restart' })}>
                        Restart
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      {c.status === 'stopped' ? (
                        <Button fullWidth variant="outlined" startIcon={<PlayArrowOutlinedIcon />} disabled={!can('start', true)} onClick={() => ask({ action: 'start', title: 'Start the stack?', text: 'Brings every container up and waits for health.', label: 'Start' })}>
                          Start
                        </Button>
                      ) : (
                        <Button fullWidth variant="outlined" color="warning" startIcon={<StopCircleOutlinedIcon />} disabled={!can('stop', c.status === 'running')} onClick={() => ask({ action: 'stop', title: 'Stop the stack?', text: 'The service is unavailable to riders and staff until you start it again. Active rides keep being billed.', label: 'Stop', danger: true })}>
                          Stop
                        </Button>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Button fullWidth variant="outlined" startIcon={<BackupOutlinedIcon />} disabled={!can('backup', live)} onClick={() => ask({ action: 'backup', title: 'Back up now?', text: 'Runs backup.sh; the archive lands in /srv/apps/' + c.slug + '/backups.', label: 'Back up' })}>
                        Backup
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button fullWidth variant="outlined" startIcon={<RocketLaunchOutlinedIcon />} disabled={!can('deploy', live)} onClick={() => setDeployOpen(true)}>
                        Deploy
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button fullWidth variant="outlined" startIcon={<KeyOutlinedIcon />} component={RouterLink} to={`/clones/${c.id}/keys`}>
                        Keys
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button fullWidth variant="outlined" startIcon={<AdminPanelSettingsOutlinedIcon />} disabled={!can('reset-admin', c.status === 'running')} onClick={() => ask({ action: 'reset-admin', title: 'Reset the super admin password?', text: 'A new password is generated and shown once. Needs a fresh 2FA code.', label: 'Reset', danger: true })}>
                        Reset admin
                      </Button>
                    </Grid>
                  </Grid>
                  <Divider />
                  <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={!can('delete', c.status !== 'deleting')} onClick={() => ask({ action: 'delete', title: `Delete ${c.name}?`, text: `Stops the stack, removes data volumes, the Caddy site and the backup cron. A final backup is kept in /srv/apps/_deleted. Needs a fresh 2FA code.`, label: 'Delete clone', danger: true, typed: c.slug })}>
                    Delete clone
                  </Button>
                </Stack>
              </SectionCard>
            )}
          </Stack>
        </Grid>
      </Grid>

      {s && (
        <>
          <Typography variant="h6" component="h2" sx={{ pt: 1 }}>
            Business{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              <RelTime iso={s.ts} prefix="· snapshot " />
            </Typography>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} lg={2}>
              <KpiTile label="Rides today" value={fmtNumber(s.rides.today)} sub={`${fmtNumber(s.rides.d7)} 7d · ${fmtNumber(s.rides.d30)} 30d`} />
            </Grid>
            <Grid item xs={6} sm={4} lg={2}>
              <KpiTile label="Revenue today" value={fmtMoney(s.revenue.today, s.currency)} sub={`${fmtMoney(s.revenue.d30, s.currency, { compact: true })} in 30d`} />
            </Grid>
            <Grid item xs={6} sm={4} lg={2}>
              <KpiTile label="Active rides" value={fmtNumber(s.activeRides)} sub={`${s.staffOnShift} staff on shift`} />
            </Grid>
            <Grid item xs={6} sm={4} lg={2}>
              <KpiTile label="Scooters available" value={`${fmtNumber(s.scooters.available)} / ${fmtNumber(s.scooters.total)}`} sub={`${s.scooters.broken} broken · ${s.scooters.offline} offline · ${s.scooters.lowBattery} low`} tone={s.scooters.available / Math.max(1, s.scooters.total) < 0.5 ? 'warning' : 'default'} />
            </Grid>
            <Grid item xs={6} sm={4} lg={2}>
              <KpiTile label="Riders" value={fmtNumber(s.riders.total)} sub={`+${fmtNumber(s.riders.new7d)} 7d · ${fmtNumber(s.riders.active30d)} active`} />
            </Grid>
            <Grid item xs={6} sm={4} lg={2}>
              <KpiTile label="Open tickets" value={fmtNumber(s.openTickets)} tone={s.openTickets > 5 ? 'warning' : 'default'} />
            </Grid>
          </Grid>
        </>
      )}

      <ConfirmDialog
        open={!!pending}
        title={pending?.title ?? ''}
        text={pending?.text}
        confirmLabel={pending?.label}
        danger={pending?.danger}
        typed={pending?.typed}
        busy={run.isPending}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
      <ConfirmDialog
        open={deployOpen}
        title="Deploy a release"
        text={`Currently on ${c.release ?? '—'}. A backup is taken first; the stack is rebuilt and verified.`}
        confirmLabel="Deploy"
        busy={run.isPending}
        confirmDisabled={!releaseId}
        onClose={() => setDeployOpen(false)}
        onConfirm={() => releaseId && run.mutate({ action: 'deploy', body: { releaseId } }, { onSettled: () => setDeployOpen(false) })}
      >
        <TextField select fullWidth label="Release" value={releaseId} onChange={(e) => setReleaseId(Number(e.target.value))} sx={{ mt: 2 }}>
          {(releases.data ?? []).map((r) => (
            <MenuItem key={r.id} value={r.id} disabled={r.status !== 'ready'}>
              {r.name} {r.status !== 'ready' ? `(${r.status})` : r.name === c.release ? '(current)' : ''}
            </MenuItem>
          ))}
        </TextField>
      </ConfirmDialog>
    </Stack>
  );
}
